// Mich-CA Portal — Shared UI Components

// ── Stat Cards ────────────────────────────────────────────────────────────────
// Standard stat card row — always rendered immediately after PageHeader
// cards: [{ label, value, sub, color, icon? }]
export function StatCards({ cards }) {
  if (!cards || cards.length === 0) return null;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.min(cards.length, 6)}, 1fr)`,
      gap: 12,
      marginBottom: 24,
    }}>
      {cards.map((c, i) => (
        <div key={i} className="card" style={{
          padding: '18px 20px',
          borderTop: `3px solid ${c.color || 'var(--accent)'}`,
          position: 'relative', overflow: 'hidden',
        }}>
          {c.icon && (
            <div style={{
              position: 'absolute', right: 14, top: 14,
              fontSize: 20, opacity: .12,
            }}>{c.icon}</div>
          )}
          <div style={{
            fontSize: 11, fontWeight: 600, color: 'var(--mid)',
            textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 8,
          }}>{c.label}</div>
          <div style={{
            fontSize: 30, fontWeight: 700, color: c.color || 'var(--accent)',
            lineHeight: 1, fontFamily: "'Roboto Mono', monospace",
          }}>{c.value ?? '—'}</div>
          {c.sub && <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 6 }}>{c.sub}</div>}
        </div>
      ))}
    </div>
  );
}


// ── Page Header ──────────────────────────────────────────────────────────────
export function PageHeader({ icon, title, subtitle, actions }) {
  return (
    <div className="page-header">
      <div>
        <div className="page-title">
          <span style={{ fontSize: 22, color: 'var(--accent)' }}>{icon}</span>
          <h1>{title}</h1>
        </div>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
    </div>
  );
}

// ── Section Title ────────────────────────────────────────────────────────────
export function SectionTitle({ children, color = 'var(--accent)' }) {
  return (
    <div className="section-title">
      <div className="section-title-bar" style={{ background: color }} />
      <span className="section-title-text" style={{ color }}>{children}</span>
    </div>
  );
}

// ── Field ────────────────────────────────────────────────────────────────────
export function Field({ label, note, required, error, children }) {
  return (
    <div className="field">
      <label className="field-label">
        {label}{required && <span className="field-required">*</span>}
      </label>
      {children}
      {note && <p className="note">{note}</p>}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

// ── Data Table ───────────────────────────────────────────────────────────────
export function DataTable({ headers, rows, accentColor = 'var(--accent)', emptyMessage = 'No data' }) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {headers.map(h => (
              <th key={h} style={{ background: `color-mix(in srgb, ${accentColor} 10%, transparent)` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} style={{ textAlign: 'center', color: 'var(--dim)', padding: '24px' }}>{emptyMessage}</td></tr>
          ) : rows.map((row, ri) => (
            <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Loading Spinner ───────────────────────────────────────────────────────────
export function Spinner({ size = 20 }) {
  return <div className="spinner" style={{ width: size, height: size }} />;
}

export function LoadingPage() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12 }}>
      <Spinner size={28} />
      <span style={{ color: 'var(--mid)', fontSize: 14 }}>Loading...</span>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────
export function EmptyState({ icon = '○', title, description, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <div style={{ fontSize: 36, marginBottom: 12, color: 'var(--dim)' }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--mid)', marginBottom: 6 }}>{title}</div>
      {description && <p style={{ fontSize: 13, color: 'var(--dim)', marginBottom: 16 }}>{description}</p>}
      {action}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: width }}>
        <div className="modal-header">
          <h2 style={{ fontSize: 16, fontWeight: 800 }}>{title}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const map = {
    active: 'tag-green', confirmed: 'tag-green',
    draft: 'tag-gold', pending: 'tag-gold', conflict: 'tag-gold',
    completed: 'tag-blue',
    suspended: 'tag-red', cancelled: 'tag-red', error: 'tag-red',
    unscheduled: 'tag-purple',
  };
  return <span className={`tag ${map[status] || 'tag-blue'}`}>{status}</span>;
}

// ── Confirmation Dialog ───────────────────────────────────────────────────────
export function ConfirmDialog({ message, title, onConfirm, onCancel, danger = false, confirmLabel, icon }) {
  const label = confirmLabel || (danger ? 'Delete' : 'Confirm');
  const iconGlyph = icon || (danger ? '⚠️' : '?');
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-body" style={{ textAlign: 'center', padding: 28 }}>
          {/* Icon is decorative only — buttons below are the interactive elements */}
          <div style={{ fontSize: 22, marginBottom: 10, opacity: 0.8 }} aria-hidden="true">{iconGlyph}</div>
          {title && <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', margin: '0 0 8px' }}>{title}</h3>}
          <p style={{ fontSize: 13, marginBottom: 22, color: 'var(--mid)', whiteSpace: 'pre-line', lineHeight: 1.5 }}>{message}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
            <button type="button" className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>
              {label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Day Picker ────────────────────────────────────────────────────────────────
export function DayPicker({ value = [], onChange }) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const toggle = (d) => onChange(value.includes(d) ? value.filter(x => x !== d) : [...value, d]);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {days.map(d => (
        <div key={d} className={`day-chip ${value.includes(d) ? 'active' : ''}`} onClick={() => toggle(d)}>{d}</div>
      ))}
    </div>
  );
}

// ── Pool Picker ───────────────────────────────────────────────────────────────
export function PoolPicker({ value = [], onChange, max = 7 }) {
  const pools = 'ABCDEFGHIJKLMNO'.split('').slice(0, max);
  const toggle = (p) => onChange(value.includes(p) ? value.filter(x => x !== p) : [...value, p]);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {pools.map(p => (
        <div key={p}
          onClick={() => toggle(p)}
          style={{
            width: 34, height: 34, borderRadius: 6, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${value.includes(p) ? 'var(--accent)' : 'var(--border)'}`,
            background: value.includes(p) ? 'rgba(0,200,150,.12)' : 'transparent',
            color: value.includes(p) ? 'var(--accent)' : 'var(--dim)',
          }}>{p}</div>
      ))}
    </div>
  );
}
